import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Users,
  BedDouble,
  AirVent,
  Waves,
  Menu,
  Globe,
  Clock
} from 'lucide-react';
import { propertyImages } from '../assets/images';

const TRANSLATIONS = {
  es: {
    nav: ['Portafolio', 'Experiencia', 'Nosotros', 'Revista'],
    explore: 'Desliza para Explorar',
    heroTitle: 'Catalogo',
    heroItalic: 'de Propiedades',
    heroSub: 'Una narrativa curada de las residencias arquitectónicas más significativas del mundo, seleccionadas por su diálogo con el paisaje.',
    baseRate: 'Tarifa Base',
    extraPersonRate: 'Tarifa Persona Extra',
    perNight: 'USD / Noche',
    taxesIncluded: 'Incluye todos los impuestos',
    cleaningFee: 'Tarifa de limpieza: $25 (no incluida)',
    narrative: 'La Narrativa',
    physicalAddress: 'Dirección Física',
    navigation: 'Navegación',
    specs: 'Especificaciones Vitales',
    guests: 'Hasta {n} Huéspedes',
    bedrooms: '{n} Suites Reales',
    airConditioners: '{n} Aire Acondicionado',
    poolPrivate: 'Piscina Privada',
    poolCommon: 'Piscina Común',
    noPool: 'Sin Acceso a Piscina',
    checkInOut: 'Entrada: {checkIn} | Salida: {checkOut}',
    footerTitle: 'Tu vitrina ',
    footerItalic: 'digital.',
    footerDesc: 'mylookbook.online es una plataforma diseñada para que creadores, profesionales y emprendedores puedan publicar y vender sus productos o servicios de forma visual y atractiva. Te permite transformar tu oferta en un sitio web listo para comercializar, facilitando que tus clientes exploren y compren lo que haces en un solo lugar.',
    contact: 'Contacto',
    inquiries: 'Consultas',
    partnerships: 'Alianzas',
    privacy: 'Privacidad',
    legal: 'Legal'
  },
  en: {
    nav: ['Portfolio', 'Experience', 'About', 'Journal'],
    explore: 'Scroll to Explore',
    heroTitle: 'Property',
    heroItalic: 'Catalog',
    heroSub: 'A curated narrative of the world\'s most significant architectural residences, selected for their dialogue with the landscape.',
    baseRate: 'Base Rate',
    extraPersonRate: 'Extra Person Rate',
    perNight: 'USD / Night',
    taxesIncluded: 'All taxes included',
    cleaningFee: 'Cleaning fee: $25 (not included)',
    narrative: 'The Narrative',
    physicalAddress: 'Physical Address',
    navigation: 'Navigation',
    specs: 'Vital Specs',
    guests: 'Up to {n} Guests',
    bedrooms: '{n} Bedroom Suites',
    airConditioners: '{n} Air Conditioners',
    poolPrivate: 'Private Pool',
    poolCommon: 'Common Pool',
    noPool: 'No Pool Access',
    checkInOut: 'Check-in: {checkIn} | Check-out: {checkOut}',
    footerTitle: 'Your digital',
    footerItalic: 'storefront.',
    footerDesc: 'mylookbook.online is a platform designed for creators, professionals, and entrepreneurs to easily showcase and sell their products or services. It allows you to transform what you offer into a beautiful, ready-to-market website, making it simple for clients to explore and purchase everything you have to sell in one place.',
    contact: 'Contact',
    inquiries: 'Inquiries',
    partnerships: 'Partnerships',
    privacy: 'Privacy',
    legal: 'Legal'
  }
};

const MOCK_PROPERTIES = [
  {
    id: 1,
    hidden: true, // Set to false to show this property again
    imageKey: 'obsidianMonolith',
    price: 60,
    extraPersonRate: 25,
    guests: 8,
    bedrooms: 3,
    airConditioners: 3,
    poolAccess: "common", // Options: "private", "common", "none"
    checkIn: "3:00 PM",
    checkOut: "12:00 PM",
    address: "14 Avenida norte, Trib Casas y Apartamentos, Santa Tecla, La Libertad",
    content: {
      en: {
        title: "Apartment with Valley View Santa Tecla",
        location: "Santa Tecla, La Libertad",
        tagline: "Modernity in a beautiful city",
        description: "Enjoy an exceptional stay at Trib Casas y Apartamentos, the ideal destination for your next trip to Santa Tecla. This exclusive apartment features 3 beds and 1 bunk bed, with capacity for 8 people. It stands out for its modern design, contemporary finishes, and a smart layout that guarantees maximum comfort during your daily rental. The apartment includes three air conditioners, living room, dining room, kitchen, refrigerator, coffee maker, and toaster. By staying here, you will have full access to the best amenities in the area: a fully equipped gym to keep up with your routine, a spectacular pool to relax under the sun, and vibrant common social areas perfect for unwinding or working. Located in a secure environment with Santa Tecla's signature cool climate and unbeatable connectivity, this space seamlessly combines the privacy of a cutting-edge home with the experience of an urban resort."
      },
      es: {
        title: "Apartamento con Vista al valle Santa Tecla",
        location: "Santa Tecla, La Libertad",
        tagline: "Modernidad en una hermosa ciudad.",
        description: "Disfruta de una estadía excepcional en Trib Casas y Apartamentos, el destino ideal para tu próximo viaje a Santa Tecla. Este exclusivo apartamento cuenta con 3 camas y 1 camarote o litera, con capacidad para 8 personas. Destaca por su diseño moderno, acabados contemporáneos y una distribución inteligente que garantiza el máximo confort durante tu alquiler diario. El apartamento incluye tres aires acondicionados, sala, comedor, cocina, refrigeradora, cafetera y tostadora. Al hospedarte aquí, tendrás acceso total a las mejores amenidades de la zona: un gimnasio completamente equipado para no interrumpir tu rutina, una espectacular piscina para relajarte bajo el sol y vibrantes espacios de esparcimiento comunes ideales para descansar o trabajar. Ubicado en un entorno seguro, con el clima fresco característico de Santa Tecla y una conectividad inmejorable, este espacio combina la privacidad de un hogar vanguardista con la experiencia de un resort urbano."
      }
    },
    images: propertyImages.obsidianMonolith
  },
  {
    id: 2,
    imageKey: 'villaAzure',
    price: 60,
    extraPersonRate: 20,
    guests: 6,
    bedrooms: 3,
    airConditioners: 3,
    poolAccess: "none", // Options: "private", "common", "none"
    checkIn: "3:00 PM",
    checkOut: "12:00 PM",
    address: 'Calle No.1, Block "B" No.10, Residencial, San Salvador, 1502, SV',
    content: {
      en: {
        title: "Lomas 500 Tower - City View San Salvador",
        location: "San Salvador, El Salvador",
        tagline: "Modern apartment close to everything",
        description: "Enjoy ultimate urban convenience in this modern apartment located in the financial and commercial heart of San Salvador. This spacious apartment accommodates up to 5 people, with an extra guest available on the sofa bed (6 total). It features full air conditioning throughout the apartment, hot water, 2 bathrooms, and a basic gym. The fully equipped kitchen includes an oven, toaster, air fryer, microwave, and refrigerator. With a prime location just minutes away from major business centers, shopping malls, entertainment hotspots, and the city's finest restaurants, this space is ideal for daily rentals. Perfect for executives and dynamic travelers, it combines comfort, security, and unbeatable connectivity to make the most of your stay in the capital."
      },
      es: {
        title: "Apartamento Torre Lomas 500 - Vista a la ciudad San Salvador",
        location: "San Salvador, El Salvador",
        tagline: "Apartamento moderno cerca de todo",
        description: "Disfruta de la máxima conveniencia urbana en este moderno apartamento ubicado en el corazón financiero y comercial de San Salvador. Este amplio apartamento tiene capacidad para 5 personas, con 1 persona extra disponible en sofá cama (6 total). Cuenta con aire acondicionado en todo el apartamento, agua caliente, 2 baños y un gimnasio super básico. La cocina completamente equipada incluye horno, tostador, freidora de aire, microondas y refrigerador. Con una ubicación privilegiada a solo minutos de los principales centros de negocios, centros comerciales, lugares de esparcimiento y los mejores restaurantes de la ciudad, este espacio es ideal para alquiler diario. Perfecto para ejecutivos y viajeros dinámicos, combina comodidad, seguridad y una conectividad inmejorable para aprovechar al máximo tu estadía en la capital."
      }
    },
    images: propertyImages.villaAzure
  },
  {
    id: 3,
    imageKey: 'sandstoneSolace',
    price: 200,
    extraPersonRate: 20,
    guests: 8,
    bedrooms: 3,
    airConditioners: 3,
    poolAccess: "common", // Options: "private", "common", "none"
    checkIn: "4:00 PM",
    checkOut: "1:00 PM",
    address: "Del Mar Apartments, Bulevar Costa del Sol, El Zapote, San Luis La Herradura",
    content: {
      en: {
        title: "SUNSET Apartment front of the beach - Costa del Sol",
        location: "Costa del Sol, El Salvador",
        tagline: "Spacious beachfront apartment with stunning sunset views.",
        description: "Experience an unmatched beachfront getaway at SUNSET apartments, located in the exclusive Costa del Sol area. This spacious, modern, and cozy apartment features 3 bedrooms with 5 beds, capacity for up to 8 guests, 3 air conditioners, and 2 bathrooms. It includes a living room, dining room, and fully equipped kitchen with refrigerator, microwave, coffee maker, and all necessary utensils. The complex stands out for its direct beach access, a spectacular common pool measuring 101 meters, and BBQ grill areas perfect for grilling. If you request in advance, we can also prepare a bonfire for you. The area offers numerous nearby restaurants and access to beautiful sandbars. The true luxury of this space is its prime location: by walking just a few meters onto the sand, you can witness front-row views of the most breathtaking sunsets on the Costa del Sol, making your stay an unforgettable experience."
      },
      es: {
        title: "SUNSET Apartamento en costa del sol frente al mar",
        location: "Costa del Sol, El Salvador",
        tagline: "Apartamento moderno frente al mar con atardeceres.",
        description: "Vive una escapada frente al mar inigualable en los apartamentos SUNSET, ubicados en la exclusiva zona de la Costa del Sol. Este amplio, moderno y acogedor apartamento cuenta con 3 habitaciones con 5 camas, capacidad para hasta 8 huéspedes, 3 aires acondicionados y 2 baños. Incluye sala, comedor y cocina completamente equipada con refrigeradora, microondas, cafetera y todos los utensilios necesarios. El complejo destaca por su acceso directo a la playa, una espectacular piscina común que mide 101 metros y áreas de asador perfectas para hacer carne. Si pides con tiempo, también te podemos preparar una fogata. La zona ofrece muchos restaurantes cercanos y acceso a los bancos de arena. El verdadero lujo de este espacio es su ubicación: con solo caminar unos pocos metros sobre la arena, podrás presenciar en primera fila los atardeceres más impresionantes de la Costa del Sol, convirtiendo tu estadía en una experiencia inolvidable."
      }
    },
    images: propertyImages.sandstoneSolace
  }
];

const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const nextSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const selectImage = (index) => {
    setCurrentIndex(index);
  };

  const scrollThumbnails = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Main Image - Full Width */}
      <div className="relative group aspect-[16/9] overflow-hidden bg-gray-100 rounded-sm shadow-xl">
        <img
          src={images[currentIndex]}
          alt={`${title} - ${currentIndex + 1}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Navigation Arrows */}
        <div className="absolute inset-0 flex items-center justify-between px-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <button onClick={prevSlide} className="p-3 bg-white/10 backdrop-blur-md text-white rounded-full hover:bg-white/30 border border-white/20 transition-all">
            <ChevronLeft size={20} />
          </button>
          <button onClick={nextSlide} className="p-3 bg-white/10 backdrop-blur-md text-white rounded-full hover:bg-white/30 border border-white/20 transition-all">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Image Counter */}
        <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnail Collage - Below Main Image - Horizontal Scroll */}
      <div className="relative">
        {/* Scroll Left Button */}
        <button
          onClick={() => scrollThumbnails('left')}
          className="absolute left-0 top-0 bottom-0 z-10 bg-gradient-to-r from-white to-transparent px-3 flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
        >
          <div className="bg-slate-900 text-white p-2 rounded-full shadow-lg">
            <ChevronLeft size={20} />
          </div>
        </button>

        {/* Scrollable Thumbnail Container - Horizontal */}
        <div
          ref={scrollContainerRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide px-12 py-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {images.map((img, idx) => (
            <div
              key={idx}
              onClick={() => selectImage(idx)}
              className={`relative flex-shrink-0 w-32 h-32 overflow-hidden bg-gray-100 rounded-sm cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                currentIndex === idx ? 'ring-2 ring-slate-900 ring-offset-2' : ''
              }`}
            >
              <img
                src={img}
                alt={`${title} thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Image number overlay */}
              <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 rounded text-xs font-medium">
                {idx + 1}
              </div>
            </div>
          ))}
        </div>

        {/* Scroll Right Button */}
        <button
          onClick={() => scrollThumbnails('right')}
          className="absolute right-0 top-0 bottom-0 z-10 bg-gradient-to-l from-white to-transparent px-3 flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
        >
          <div className="bg-slate-900 text-white p-2 rounded-full shadow-lg">
            <ChevronRight size={20} />
          </div>
        </button>
      </div>
    </div>
  );
};

const LookbookCard = ({ property, lang }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const t = TRANSLATIONS[lang];
  const content = property.content[lang];

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <article 
      ref={cardRef}
      className={`max-w-5xl mx-auto mb-40 transition-all duration-1000 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      <ImageCarousel images={property.images} title={content.title} />

      <div className="mt-12 px-6 sm:px-0">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-100 pb-10 mb-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-slate-400 uppercase tracking-[0.3em] text-[10px] font-bold mb-3">
              <MapPin size={12} className="text-slate-300" />
              {content.location}
            </div>
            <h2 className="text-4xl md:text-6xl font-serif text-slate-900 leading-tight">
              {content.title}
            </h2>
            <p className="mt-4 text-xl font-light text-slate-500 italic font-serif">
              "{content.tagline}"
            </p>
          </div>
          
          <div className="text-right md:min-w-[240px]">
            <div className="mb-4">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">{t.baseRate}</span>
              <span className="text-4xl font-serif text-slate-900">${property.price}</span>
              <span className="text-slate-400 ml-2 text-sm font-medium">{t.perNight}</span>
            </div>
            {property.extraPersonRate && (
              <div className="pt-3 border-t border-slate-100 mb-4">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">{t.extraPersonRate}</span>
                <span className="text-2xl font-serif text-slate-900">${property.extraPersonRate}</span>
                <span className="text-slate-400 ml-2 text-sm font-medium">{t.perNight}</span>
              </div>
            )}
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <p className="text-xs text-slate-500 italic leading-relaxed">
                {t.taxesIncluded}
              </p>
              <p className="text-xs text-slate-500 italic leading-relaxed">
                {t.cleaningFee}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8">
            <div className="mb-12">
              <h3 className="text-[11px] uppercase tracking-[0.3em] font-bold text-slate-400 mb-6">{t.narrative}</h3>
              <p className="text-slate-600 text-lg leading-relaxed font-light mb-12">
                {content.description}
              </p>

              {}
              <div className="pt-10 border-t border-slate-100">
                <div className="mb-8">
                  <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400 block mb-3">{t.physicalAddress}</span>
                  <p className="text-slate-900 font-serif text-2xl leading-snug">
                    {property.address}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-8">
                  <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400">{t.navigation}:</span>
                  <div className="flex items-center gap-10">
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`}
                      target="_blank"
                      className="text-[12px] uppercase tracking-widest font-bold text-slate-900 hover:text-slate-500 transition-all flex items-center gap-2 group"
                    >
                      Google Maps <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </a>
                    <a 
                      href={`https://waze.com/ul?q=${encodeURIComponent(property.address)}&navigate=yes`}
                      target="_blank"
                      className="text-[12px] uppercase tracking-widest font-bold text-slate-900 hover:text-slate-500 transition-all flex items-center gap-2 group"
                    >
                      Waze <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 bg-slate-50 p-10 rounded-sm self-start">
            <h3 className="text-[11px] uppercase tracking-[0.3em] font-bold text-slate-900 mb-8 border-b border-slate-200 pb-4">{t.specs}</h3>
            <ul className="space-y-6">
              <li className="flex items-center gap-5 text-slate-600">
                <Users size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">{t.guests.replace('{n}', property.guests)}</span>
              </li>
              <li className="flex items-center gap-5 text-slate-600">
                <BedDouble size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">{t.bedrooms.replace('{n}', property.bedrooms)}</span>
              </li>
              <li className="flex items-center gap-5 text-slate-600">
                <AirVent size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">{t.airConditioners.replace('{n}', property.airConditioners)}</span>
              </li>
              <li className="flex items-center gap-5 text-slate-600">
                <Waves size={20} className="text-slate-400" />
                <span className="text-sm tracking-wide">
                  {property.poolAccess === 'private' && t.poolPrivate}
                  {property.poolAccess === 'common' && t.poolCommon}
                  {property.poolAccess === 'none' && t.noPool}
                </span>
              </li>
              {property.checkIn && property.checkOut && (
                <li className="flex items-center gap-5 text-slate-600">
                  <Clock size={20} className="text-slate-400" />
                  <span className="text-sm tracking-wide">
                    {t.checkInOut.replace('{checkIn}', property.checkIn).replace('{checkOut}', property.checkOut)}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

export default function LookbookPacifico() {
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState('es');
  const t = TRANSLATIONS[lang];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-sans text-slate-900 selection:bg-slate-900 selection:text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;600;700&display=swap');
        .font-serif { font-family: 'Playfair Display', serif; }
        .font-sans { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 px-8 md:px-16 flex items-center justify-between ${
        scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-100 py-5 shadow-sm' : 'bg-transparent py-8'
      }`}>
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="w-10 h-10 bg-slate-900 flex items-center justify-center text-white font-serif text-2xl font-bold">M</div>
          <span className="text-[13px] tracking-[0.15em] font-bold text-slate-900 lowercase">
            mylookbook<span className="text-slate-400 font-light">.online</span>
          </span>
        </Link>

        <div className="flex items-center gap-6">
          {/* Language Switcher */}
          <div className="flex items-center gap-2 border border-slate-200 rounded-full px-3 py-1 bg-white/50 backdrop-blur-sm">
            <Globe size={14} className="text-slate-400" />
            <button
              onClick={() => setLang('es')}
              className={`text-[10px] font-bold tracking-widest ${lang === 'es' ? 'text-slate-900 underline underline-offset-4' : 'text-slate-400 hover:text-slate-600'}`}
            >
              ES
            </button>
            <span className="text-slate-200 text-[10px]">|</span>
            <button
              onClick={() => setLang('en')}
              className={`text-[10px] font-bold tracking-widest ${lang === 'en' ? 'text-slate-900 underline underline-offset-4' : 'text-slate-400 hover:text-slate-600'}`}
            >
              EN
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="pt-48 pb-24 px-8 text-center max-w-3xl mx-auto">
        <h1 className="text-6xl md:text-8xl font-serif text-slate-900 mb-8 leading-[1.1]">
          {t.heroTitle} <span className="italic">{t.heroItalic}</span>.
        </h1>
        {/*<p className="text-slate-500 text-xl font-light leading-relaxed mb-16 max-w-xl mx-auto">
          {t.heroSub}
        </p>
        <div className="flex flex-col items-center gap-6">
          <div className="w-px h-24 bg-gradient-to-b from-slate-900 to-transparent"></div>
          <span className="text-[10px] uppercase tracking-[0.4em] text-slate-400 font-bold">{t.explore}</span>
        </div>*/}
      </header> 

      {/* Main Feed */}
      <main className="px-8 md:px-16">
        {MOCK_PROPERTIES.filter(prop => !prop.hidden).map((property) => (
          <LookbookCard key={property.id} property={property} lang={lang} />
        ))}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-32 px-8 mt-20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-20 border-b border-white/10 pb-20">
          <div>
            <h2 className="text-4xl font-serif mb-8 leading-tight">{t.footerTitle} <br/><span className="italic">{t.footerItalic}</span></h2>
            <p className="text-slate-400 font-light leading-relaxed max-w-md text-lg">
              {t.footerDesc}
            </p>
          </div>
          <div className="flex flex-col justify-end">
            <div className="grid grid-cols-2 gap-16">
              <div>
                <h4 className="text-[10px] uppercase tracking-widest font-bold text-white mb-6">{t.contact}</h4>
                <ul className="space-y-3 text-slate-400 text-sm">
                  <li className="hover:text-white transition-colors cursor-pointer italic">{t.inquiries}</li>
                  <li className="hover:text-white transition-colors cursor-pointer italic">{t.partnerships}</li>
                </ul>
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-widest font-bold text-white mb-6">Social</h4>
                <ul className="space-y-3 text-slate-400 text-sm">
                  <li className="hover:text-white transition-colors cursor-pointer italic">Instagram</li>
                  <li className="hover:text-white transition-colors cursor-pointer italic">LinkedIn</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-16 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] uppercase tracking-[0.3em] font-bold text-slate-500">
          <span>&copy; 2026 mylookbook.online</span>
          <div className="flex gap-10">
            <span className="hover:text-white cursor-pointer transition-colors">{t.privacy}</span>
            <span className="hover:text-white cursor-pointer transition-colors">{t.legal}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Made with Bob
